import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * BulkUploadInput - Hidden file input for bulk uploading photos
 *
 * Parses structured filenames to determine object type and ID:
 * - structure_123_1701234567890.jpg -> structure ID 123
 * - UPRN_10012345678_1701234567890.jpg -> UPRN 10012345678
 * - Legacy: jb_456_1701234567890.jpg, mh_456_1701234567890.jpg, pole_456_1701234567890.jpg
 *
 * Files without matching filenames will be rejected.
 */
import { useRef, useState } from 'react';
import { addCompletedBulkUploads } from '../../services/uploadStorage';
// Filename pattern for bulk uploads - must match server-side pattern in upload.js
const FILENAME_PATTERN = /^(jb|mh|pole|structure|UPRN)_([a-zA-Z0-9]+)_([0-9]+)\.(jpg|jpeg|png)$/i;
export const BulkUploadInput = ({ onUploadStart, onUploadComplete, }) => {
    const inputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const validateFilename = (filename) => {
        return FILENAME_PATTERN.test(filename);
    };
    const handleFileChange = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0)
            return;
        // Validate filenames before uploading
        const validFiles = [];
        const invalidFiles = [];
        Array.from(files).forEach((file) => {
            if (validateFilename(file.name)) {
                validFiles.push(file);
            }
            else {
                invalidFiles.push(file.name);
            }
        });
        if (invalidFiles.length > 0) {
            const invalidList = invalidFiles.slice(0, 5).join('\n');
            const moreCount = invalidFiles.length > 5 ? `\n...and ${invalidFiles.length - 5} more` : '';
            alert(`The following files have invalid filenames and will be skipped:\n\n${invalidList}${moreCount}\n\n` +
                `Valid format: structure_ID_TIMESTAMP.jpg or UPRN_UPRN_TIMESTAMP.jpg\n` +
                `Example: structure_123_1701234567890.jpg`);
        }
        if (validFiles.length === 0) {
            // Reset input
            if (inputRef.current)
                inputRef.current.value = '';
            return;
        }
        // Confirm upload
        const confirmMessage = `Upload ${validFiles.length} file(s)?` +
            (invalidFiles.length > 0 ? `\n\n(${invalidFiles.length} files with invalid names will be skipped)` : '');
        if (!window.confirm(confirmMessage)) {
            if (inputRef.current)
                inputRef.current.value = '';
            return;
        }
        setIsUploading(true);
        onUploadStart?.();
        const formData = new FormData();
        // Don't set objectType or objectID - let server parse from filenames
        validFiles.forEach((file) => {
            formData.append('mFiles', file);
        });
        try {
            const response = await fetch('/upload/multi', {
                method: 'POST',
                body: formData,
                headers: {
                    'Accept': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: Upload request failed`);
            }
            const result = await response.json();
            // Server returns uploads array with: id, filename, url, size, mimetype
            const serverUploads = result.uploads || [];
            const successResults = serverUploads.map((u) => ({
                filename: u.filename,
                success: true,
                id: u.id,
                url: u.url,
            }));
            const errorResults = (result.errors || []).map((e) => ({
                filename: typeof e === 'string' ? e : e.filename,
                success: false,
                error: typeof e === 'string' ? e : e.error,
            }));
            // Store completed uploads in IndexedDB for display in My Uploads
            if (serverUploads.length > 0) {
                try {
                    await addCompletedBulkUploads(serverUploads);
                    console.log('[BulkUploadInput] Stored', serverUploads.length, 'completed uploads in IndexedDB');
                }
                catch (err) {
                    console.error('[BulkUploadInput] Failed to store completed uploads:', err);
                }
            }
            // Show results
            const successCount = successResults.length;
            const errorCount = errorResults.length;
            if (errorCount === 0) {
                alert(`Successfully uploaded ${successCount} file(s).`);
            }
            else if (successCount === 0) {
                alert(`All ${errorCount} uploads failed.\n\nErrors:\n${errorResults.map(e => `${e.filename}: ${e.error}`).join('\n')}`);
            }
            else {
                alert(`Upload complete:\n` +
                    `- ${successCount} succeeded\n` +
                    `- ${errorCount} failed\n\n` +
                    `Errors:\n${errorResults.map(e => `${e.filename}: ${e.error}`).join('\n')}`);
            }
            onUploadComplete?.({ success: successResults, errors: errorResults });
        }
        catch (error) {
            console.error('[BulkUploadInput] Upload error:', error);
            alert(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
            onUploadComplete?.({ success: [], errors: [] });
        }
        finally {
            setIsUploading(false);
            // Reset input so same files can be selected again
            if (inputRef.current)
                inputRef.current.value = '';
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx("input", { ref: inputRef, id: "mFiles", type: "file", accept: "image/jpeg,image/png,.jpg,.jpeg,.png", multiple: true, style: { display: 'none' }, onChange: handleFileChange, disabled: isUploading }), isUploading && (_jsx("div", { style: {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000,
                }, children: _jsxs("div", { style: {
                        backgroundColor: 'var(--card, white)',
                        color: 'var(--card-foreground, black)',
                        padding: '2rem',
                        borderRadius: '0.5rem',
                        textAlign: 'center',
                    }, children: [_jsx("div", { style: {
                                width: '48px',
                                height: '48px',
                                border: '4px solid var(--muted, #e5e7eb)',
                                borderTopColor: 'var(--primary, #2563eb)',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                margin: '0 auto 1rem',
                            } }), _jsx("p", { children: "Uploading files..." })] }) }))] }));
};
export default BulkUploadInput;
